import * as React from 'react'
import { Navigate } from 'react-router'

import LoadingScreen from '@/components/misc/LoadingScreen'
import useAppSelector from '@/hooks/useAppSelector'
import { useAuth } from '@/hooks/useAuth'
import { useHomeRoute } from '@/hooks/useHomeRoute'
import { useIsRoute } from '@/hooks/useIsRoute'
import { useShouldRedirectToReviewAgreementTemplateRevisionsPage } from '@/hooks/useShouldRedirectToReviewAgreementTemplateRevisionsPage'
import { COMPANY_STATUS } from '@/lib/constants'
import { tokenFromSnapshotUrl } from '@/lib/helpers/helperFunctions'
import { ROUTES } from '@/lib/routes'
import { selectCurrentUserType } from '@/redux/slices/session'
import { UserType } from '@/types/misc'

interface Props {
  children: React.ReactNode | ((props: RenderProps) => React.ReactNode)
  guard?: (context: { currentUserType?: UserType }) => boolean
  requiredUserType?: UserType
}

interface RenderProps {
  currentUserType?: UserType
}

// Use to prevent unauthenticated users from accessing a route
const AuthGuard: React.FC<Props> = ({ children, guard, requiredUserType }) => {
  const {
    currentUser,
    currentCompany,
    isAuthenticated,
    isInitialized,
    isCurrentCompanyDomainLoginGranted,
  } = useAuth()
  const homeRoute = useHomeRoute()
  const isRoute = useIsRoute()
  const currentUserType = useAppSelector(selectCurrentUserType)
  const shouldRedirectToReviewAgreementTemplateRevisionsPage =
    useShouldRedirectToReviewAgreementTemplateRevisionsPage()

  if (!isInitialized) {
    return <LoadingScreen />
  }

  const isGuest = !isAuthenticated

  if (isGuest) {
    const token = tokenFromSnapshotUrl(window.location.pathname)
    const currentUri = window.location.href

    /**
     * Special case for the intelliflo redirect page where we want to redirect
     * users to the register page, not the login page.
     * @todo: Allow configuring such special cases via a component prop.
     */
    if (isRoute(ROUTES._intellifloIntegrationRedirect)) {
      return (
        <Navigate
          to={ROUTES.signUp({ searchParams: { redirectUri: currentUri } })}
          replace
        />
      )
    }

    return (
      <Navigate
        to={ROUTES.signIn({
          searchParams: { token, redirectUri: currentUri },
        })}
        replace
      />
    )
  }

  // Redirect if domain login is not allowed
  if (
    !isCurrentCompanyDomainLoginGranted &&
    ![
      ROUTES.domainAccessDenied(),
      ROUTES.companyRegistration(),
      ROUTES.invitations(),
    ].includes(window.location.pathname)
  ) {
    return <Navigate to={ROUTES.domainAccessDenied()} replace />
  }

  // Doesn't have the required userType
  if (requiredUserType && currentUserType !== requiredUserType) {
    return <Navigate to={homeRoute} />
  }

  // Doesn't satisfy the guard requirement
  if (typeof guard === 'function' && !guard({ currentUserType })) {
    return <Navigate to={homeRoute} />
  }

  const isNewUser = currentUser && !currentUserType

  if (
    isNewUser &&
    ![
      ROUTES.redirectNewUser(),
      ROUTES.invitations(),
      ROUTES.companyRegistration(),
    ].includes(window.location.pathname)
  ) {
    return <Navigate to={ROUTES.redirectNewUser()} />
  }

  const isSuspended =
    isAuthenticated && currentCompany?.status === COMPANY_STATUS.SUSPENDED

  if (isSuspended && window.location.pathname !== ROUTES.companySuspended()) {
    return <Navigate to={ROUTES.companySuspended()} />
  }

  if (shouldRedirectToReviewAgreementTemplateRevisionsPage) {
    return (
      <Navigate to={ROUTES.commercialAgreementTemplateRevisions()} replace />
    )
  }

  return typeof children === 'function'
    ? children({ currentUserType })
    : children
}

export default AuthGuard
